import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Rating,
  Avatar,
  Paper,
  Tabs,
  Tab,
  Chip,
  Divider,
  Stack,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import Store from '@mui/icons-material/Store';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import LocationOn from '@mui/icons-material/LocationOn';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Message from '@mui/icons-material/Message';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Star from '@mui/icons-material/Star';
import Send from '@mui/icons-material/Send';
import Close from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { mockProducts, Product } from '../data/mockMarketplace';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`seller-tabpanel-${index}`}
      aria-labelledby={`seller-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SellerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [seller, setSeller] = useState<any | null>(null);
  const [sellerProducts, setSellerProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);

  // Mock Reviews
  const reviews = [
    { id: 1, user: 'John Doe', rating: 5, date: '2023-10-15', comment: 'Excellent quality seeds! Germination rate was nearly 100%.' },
    { id: 2, user: 'Alice Smith', rating: 4, date: '2023-09-28', comment: 'Fast delivery and good packaging. Will order again.' },
    { id: 3, user: 'Robert Farmer', rating: 5, date: '2023-09-10', comment: 'Very knowledgeable seller. Helped me choose the right fertilizer.' },
    { id: 4, user: 'Sarah Green', rating: 3, date: '2023-08-05', comment: 'Product is good but delivery took longer than expected.' },
  ];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      // In a real app, we'd fetch seller by ID. Here we find a product with this seller ID.
      // Assuming id param is seller ID (string)
      const foundProduct = mockProducts.find(p => p.seller.id === id);

      if (foundProduct) {
        setSeller(foundProduct.seller);
        // Find all products by this seller
        const products = mockProducts.filter(p => p.seller.id === id);
        setSellerProducts(products);
      }
      setLoading(false);
    }, 500);
    window.scrollTo(0, 0);
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleContactOpen = () => {
    setContactOpen(true);
    setMessageSent(false);
    setMessage('');
  };

  const handleContactClose = () => {
    setContactOpen(false);
  };

  const handleSendMessage = () => {
    // Simulate sending message
    setMessageSent(true);
    setTimeout(() => {
      setContactOpen(false);
      // Could show a snackbar here
    }, 2000);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 4 }} />
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="text" height={40} width="60%" />
            <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!seller) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Seller not found</Typography>
        <Button variant="contained" onClick={() => navigate('/marketplace')}>
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Seller Header / Cover */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          height: 200,
          bgcolor: 'primary.main',
          borderRadius: 2,
          mb: 8,
          background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
          color: 'white',
        }}
      >
        <Box sx={{ position: 'absolute', bottom: -40, left: 40, display: 'flex', alignItems: 'flex-end', gap: 3 }}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              border: '4px solid white',
              bgcolor: 'secondary.main',
              fontSize: 48,
              boxShadow: 3,
            }}
          >
            {seller.name[0]}
          </Avatar>
          <Box sx={{ mb: 1, color: 'text.primary' }}>
            {/* Empty space filler, content is below */}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={4}>
        {/* Left Sidebar: Seller Info */}
        <Grid item xs={12} md={4}>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                {seller.name}
              </Typography>
              {seller.verified && (
                <VerifiedUser color="primary" />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Rating value={seller.rating} readOnly precision={0.5} />
              <Typography variant="body2" color="text.secondary">
                ({seller.rating} rating)
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <LocationOn color="action" />
                <Typography>{seller.location}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <CalendarToday color="action" />
                <Typography>Joined {new Date(seller.joinedDate).toLocaleDateString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Message color="action" />
                <Typography>Response Rate: {seller.responseRate}</Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<Message />}
              sx={{ mb: 2 }}
              onClick={handleContactOpen}
            >
              Negotiate Secure Deal
            </Button>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Tooltip title="For your protection, all communications and payments must be kept on the platform.">
                <Chip 
                  icon={<LockIcon sx={{ '&&': { fontSize: 14 } }} />} 
                  label="Secure Platform Isolation" 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              </Tooltip>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
              <Typography variant="h6" gutterBottom>About</Typography>
              <Typography variant="body2" color="text.secondary">
                We are dedicated to providing the freshest produce directly from our farm to your table.
                All our products are organic and sustainably grown.
              </Typography>
            </Paper>
          </Box>
        </Grid>

        {/* Right Content: Products & Reviews */}
        <Grid item xs={12} md={8}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Products (${sellerProducts.length})`} />
              <Tab label="Reviews" />
              <Tab label="Policies" />
            </Tabs>
          </Box>

          <CustomTabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {sellerProducts.map((product) => (
                <Grid key={product.id} item xs={12} sm={6}>
                  <ProductCardEnhanced
                    product={product}
                    onAddToCart={addToCart}
                    onToggleFavorite={() => toggleWishlist(product)}
                    isFavorite={isInWishlist(product.id)}
                    onQuickView={() => navigate(`/marketplace/product/${product.id}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={tabValue} index={1}>
            <Box sx={{ maxWidth: 800 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  {seller.rating}
                </Typography>
                <Rating value={seller.rating} precision={0.1} readOnly size="large" />
                <Typography color="text.secondary">
                  Based on {reviews.length} reviews
                </Typography>
              </Box>

              <List>
                {reviews.map((review) => (
                  <React.Fragment key={review.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>{review.user[0]}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {review.user}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(review.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Rating value={review.rating} readOnly size="small" sx={{ my: 0.5 }} />
                            <Typography variant="body2" color="text.primary">
                              {review.comment}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={tabValue} index={2}>
            <Box sx={{ maxWidth: 800 }}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">Shipping & Delivery</Typography>
                <Typography paragraph>
                  <strong>Processing Time:</strong> Orders are processed within 24 hours of payment confirmation.
                </Typography>
                <Typography paragraph>
                  <strong>Delivery Areas:</strong> We deliver nationwide. Remote areas may require additional time.
                </Typography>
                <Typography paragraph>
                  <strong>Shipping Rates:</strong> Calculated based on weight and distance at checkout. Free shipping for orders over KES 50,000.
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">Returns & Refunds</Typography>
                <Typography paragraph>
                  <strong>Return Window:</strong> You have 48 hours to inspect your goods upon delivery.
                </Typography>
                <Typography paragraph>
                  <strong>Eligibility:</strong> Returns are accepted for damaged goods, wrong items, or quality issues. Perishable goods must be reported immediately.
                </Typography>
                <Typography paragraph>
                  <strong>Refund Process:</strong> Refunds are processed to the original payment method within 3-5 business days after approval.
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">Payment Methods</Typography>
                <Typography>
                  We accept M-PESA, Visa, Mastercard, and Bank Transfer. All payments are secured through the Farmers Hub platform.
                </Typography>
              </Paper>
            </Box>
          </CustomTabPanel>
        </Grid>
      </Grid>

      {/* Contact Seller Dialog */}
      <Dialog open={contactOpen} onClose={handleContactClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Contact {seller.name}
          <IconButton
            aria-label="close"
            onClick={handleContactClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {messageSent ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Message Sent!</Typography>
              <Typography color="text.secondary">
                The seller will respond to you shortly via the B2B Deal Room.
              </Typography>
            </Box>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
                <strong>Platform Protection Policy:</strong> To ensure your deal is insured and protected by Farmers HUB, please do not share phone numbers or personal emails.
              </Alert>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                Send a message to inquire about products, bulk orders, or shipping.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="message"
                label="Your Message"
                type="text"
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={message}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMessage(e.target.value)}
                placeholder="Hi, I'm interested in..."
              />
            </>
          )}
        </DialogContent>
        {!messageSent && (
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleContactClose}>Cancel</Button>
            <Button
              onClick={handleSendMessage}
              variant="contained"
              endIcon={<Send />}
              disabled={!message.trim()}
            >
              Send Message
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default SellerProfile;
